import React, { useState } from 'react';
import { toast } from 'sonner';
import { Check } from 'lucide-react';
import { axiosInstance } from '../../App';
import InfoTip from '../InfoTip';

const TIPS = {
  floor: 'Floor Leaders answer on Floor Space with a Floor Leader badge (ranked first), can mark questions answered, and see the escalation queue.',
  source: 'Source Leaders can view and edit (not delete) knowledge in their scope, and get its review reminders and "Needs attention". Whole organisation covers all knowledge.',
};
const LABELS = { floor: 'Floor Leader', source: 'Source Leader' };

export const leaderScope = (user, kind) => {
  const explicit = user[`${kind}_leader_scope`];
  if (['off', 'organisation', 'teams'].includes(explicit)) return explicit;
  const ids = user[kind === 'floor' ? 'floor_leader_team_ids' : 'source_leader_team_ids'] || [];
  if (ids.length) return 'teams';
  if (kind === 'floor') return user.is_floor_leader ? 'organisation' : 'off';
  return user.is_source_lead ? 'teams' : 'off';
};

// Off / Whole organisation / Specific teams for one leader role. Only the Tenant Admin can change it.
const LeadershipControl = ({ user, kind, teams, canEdit, onSaved }) => {
  const teamKey = kind === 'floor' ? 'floor_leader_team_ids' : 'source_leader_team_ids';
  const [scope, setScope] = useState(leaderScope(user, kind));
  const [teamIds, setTeamIds] = useState(user[teamKey] || []);
  const [saving, setSaving] = useState(false);

  const save = async (nextScope, nextTeams = teamIds) => {
    if (nextScope === 'teams' && !nextTeams.length) { setScope('teams'); return; }  // Wait for a team to be picked.
    setSaving(true);
    try {
      await axiosInstance.put(`/users/${user.id}/leadership/${kind}`, { scope: nextScope, team_ids: nextScope === 'teams' ? nextTeams : [] });
      setScope(nextScope);
      setTeamIds(nextScope === 'teams' ? nextTeams : []);
      toast.success(`${LABELS[kind]} updated`);
      onSaved?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Could not update ${LABELS[kind]}`);
    } finally { setSaving(false); }
  };
  const toggleTeam = (id) => {
    const next = teamIds.includes(id) ? teamIds.filter((value) => value !== id) : [...teamIds, id];
    setTeamIds(next);
    save(next.length ? 'teams' : 'off', next);
  };

  const options = [['off', 'Off'], ['organisation', 'Whole organisation'], ['teams', 'Specific teams']];
  return <div data-testid={`leadership-${kind}`}>
    <div className="mb-1 flex items-center gap-1 text-xs font-medium text-slate-500">{LABELS[kind]}<InfoTip label={`About ${LABELS[kind]}`}>{TIPS[kind]}</InfoTip></div>
    {canEdit ? <>
      <div className="inline-flex overflow-hidden rounded-md border border-slate-200 bg-white text-xs" role="group" aria-label={`${LABELS[kind]} scope`}>
        {options.map(([value, label]) => <button key={value} type="button" disabled={saving} aria-pressed={scope === value} onClick={() => (value === 'teams' ? setScope('teams') : save(value))}
          className={`px-2.5 py-1.5 ${scope === value ? 'qflow-accent-bg' : 'text-slate-600 hover:bg-slate-50'}`}>{label}</button>)}
      </div>
      {scope === 'teams' && <div className="mt-2 flex max-w-sm flex-wrap gap-1.5">
        {teams.length ? teams.map((team) => { const on = teamIds.includes(team.id); return <button key={team.id} type="button" disabled={saving} onClick={() => toggleTeam(team.id)}
          className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${on ? 'border-sky-600 bg-sky-50 text-sky-700' : 'border-slate-200 bg-white text-slate-600'}`}>{on && <Check size={11} />}{team.name}</button>; })
          : <span className="text-xs text-slate-500">No teams yet. Use Whole organisation, or create teams first.</span>}
      </div>}
    </> : <span className="inline-flex h-8 items-center rounded-md border border-slate-200 bg-white px-2.5 text-xs text-slate-600" title="Only the Tenant Admin can change this">
      {scope === 'off' ? 'Off' : scope === 'organisation' ? 'Whole organisation' : `${teamIds.length} team${teamIds.length === 1 ? '' : 's'}`}
    </span>}
  </div>;
};

export default LeadershipControl;
